import { nhReferralPartners } from "@/lib/northHoustonData";
import { ArrowRight } from "lucide-react";

const trackStyles = {
  family: { label: "Family Track", border: "border-[#0A192F]", dot: "bg-[#0A192F]" },
  senior: { label: "Senior Track", border: "border-[#8B0000]", dot: "bg-[#8B0000]" },
  both: { label: "Both Tracks", border: "border-[#D4AF37]", dot: "bg-[#D4AF37]" },
};

export default function ReferralNetworkNH() {
  return (
    <section
      id="network"
      data-testid="section-referral-network-nh"
      className="bg-[#0A192F] text-[#F9F7F1] py-24 lg:py-32 relative overflow-hidden"
    >
      <div className="absolute inset-0 grain-overlay opacity-30" aria-hidden />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#D4AF37]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
                04 — The Coordinated Referral Network
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
              Active referrals.<br />
              <em className="italic text-[#D4AF37]">Not passive paperwork.</em>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-2 space-y-4 text-[#CBD5E1] leading-relaxed">
            <p>
              The Bridge connector does not hand a district resident a flyer
              and a phone number. We hold the relationships. We make warm
              handoffs. We walk the client to the registrar's office at Lone
              Star North Harris. We sit in on the Workforce Solutions childcare
              intake. We schedule the Harris County RIDES pickup for the
              endocrinology appointment ourselves. We make the introduction at
              the Amazon hiring event.
            </p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm pt-4">
              <div className="flex items-start gap-3">
                <span className="font-display text-2xl text-[#D4AF37] leading-none">A</span>
                <p>
                  <strong className="text-[#F9F7F1]">Active referral</strong> — the
                  connector personally introduces, schedules, and follows up until the
                  client is enrolled.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-display text-2xl text-[#CBD5E1] leading-none">P</span>
                <p>
                  <strong className="text-[#F9F7F1]">Passive referral</strong> — the
                  client is handed contact info and expected to navigate alone.
                  <span className="block text-[#8B0000] font-mono text-[10px] uppercase tracking-widest mt-1">
                    We don't do this.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Hub + spokes layout */}
        <div className="grid lg:grid-cols-12 gap-4">
          <div className="lg:col-span-4 bg-[#8B0000] p-8 lg:p-10 border border-[#8B0000] flex flex-col justify-between min-h-[260px]">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#F9F7F1]/80">
              The Hub
            </div>
            <div>
              <div className="font-display text-3xl lg:text-4xl leading-tight">
                North Houston District<br />Service Connector
              </div>
              <p className="mt-4 text-sm text-[#F9F7F1]/85">
                Single point of accountability. One human relationship across every
                service line, for 12–24 months minimum.
              </p>
            </div>
          </div>

          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-[#F9F7F1]/15 border border-[#F9F7F1]/15">
            {nhReferralPartners.map((p) => {
              const s = trackStyles[p.track];
              return (
                <div
                  key={p.name}
                  data-testid={`partner-nh-${p.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                  className="bg-[#0A192F] p-5 lg:p-6 group transition-colors hover:bg-[#15243F]"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                    <span className="font-mono text-[9px] uppercase tracking-widest text-[#94A3B8]">
                      {s.label}
                    </span>
                  </div>
                  <div className="font-display text-lg text-[#F9F7F1] flex items-center justify-between">
                    {p.name}
                    <ArrowRight className="h-4 w-4 text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <p className="text-xs text-[#CBD5E1] mt-2 leading-relaxed">{p.role}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
